import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { CoffeeBadge } from './index';
import { CoffeeTypeRating, interpolateRatingColor, useCoffeesByIds } from '../hooks/useCoffeeShops';
import CoffeeCardWeb from '../../coffee_card_web/CoffeeCardWeb';

interface ShopRatingsCollapsedProps {
    shopId: string;
    coffeeRatings: { [coffeeType: string]: CoffeeTypeRating };
    totalRatings: number;
}

const STORAGE_KEY_PREFIX = 'coffee_shop_detail_';

// expanded by default; only collapsed if the user explicitly closed it for this shop
function readExpandedPref(shopId: string): boolean {
    try {
        const raw = window.localStorage.getItem(`${STORAGE_KEY_PREFIX}${shopId}__ratings_expanded`);
        return raw === null ? true : raw === '1';
    } catch {
        return true;
    }
}

function writeExpandedPref(shopId: string, value: boolean): void {
    try {
        window.localStorage.setItem(`${STORAGE_KEY_PREFIX}${shopId}__ratings_expanded`, value ? '1' : '0');
    } catch {
        /* ignore */
    }
}

const ShopRatingsCollapsed: React.FC<ShopRatingsCollapsedProps> = ({ shopId, coffeeRatings, totalRatings }) => {
    const [open, setOpen] = useState(() => readExpandedPref(shopId));
    const coffeeTypes = Object.keys(coffeeRatings);
    const [selectedType, setSelectedType] = useState<string | null>(coffeeTypes[0] ?? null);

    useEffect(() => {
        if (coffeeTypes.length > 0 && !selectedType) {
            setSelectedType(coffeeTypes[0]);
        }
    }, [coffeeTypes, selectedType]);

    const allRatingIds = useMemo(() => {
        const ids: string[] = [];
        Object.values(coffeeRatings).forEach(ct => ct.ratings.forEach(r => ids.push(r._id)));
        return ids;
    }, [coffeeRatings]);

    const { coffees: fullCoffeeEntries } = useCoffeesByIds(open ? allRatingIds : []);
    const currentTypeData = selectedType ? coffeeRatings[selectedType] : null;

    const handleToggle = () => {
        const next = !open;
        setOpen(next);
        writeExpandedPref(shopId, next);
    };

    if (totalRatings === 0) {
        return (
            <section className="border-brutal border-ink shadow-brutal-sm rounded-sm bg-paper p-6 text-center">
                <h3 className="text-lg font-black text-ink uppercase tracking-tight mb-1">No ratings yet</h3>
                <p className="text-ink/70 text-sm">Be the first to rate a coffee here.</p>
            </section>
        );
    }

    return (
        <section className="border-brutal border-ink shadow-brutal-sm rounded-sm bg-paper overflow-hidden">
            <button
                type="button"
                onClick={handleToggle}
                className="w-full flex items-center justify-between p-4 hover:bg-mint/20 transition-colors"
                aria-expanded={open}
            >
                <div className="flex items-baseline gap-3">
                    <h2 className="text-lg sm:text-xl font-black text-ink uppercase tracking-tight">Recent ratings</h2>
                    <span className="text-xs font-bold uppercase tracking-widest text-ink/60">
                        {totalRatings} · {coffeeTypes.length} {coffeeTypes.length === 1 ? 'type' : 'types'}
                    </span>
                </div>
                <FontAwesomeIcon
                    icon={open ? faChevronUp : faChevronDown}
                    className="text-ink"
                />
            </button>

            {open && (
                <div className="px-4 pb-4 pt-4 border-t-2 border-ink">
                    <div className="flex flex-wrap gap-2 mb-5 pb-4 border-b border-ink/15">
                        {coffeeTypes.map(type => {
                            const typeData = coffeeRatings[type];
                            return (
                                <CoffeeBadge
                                    key={type}
                                    coffeeKey={type}
                                    displayName={typeData.displayName}
                                    iconName={typeData.iconName}
                                    rating={typeData.averageRating}
                                    count={typeData.totalRatings}
                                    size="sm"
                                    selected={selectedType === type}
                                    onClick={() => setSelectedType(type)}
                                />
                            );
                        })}
                    </div>

                    {currentTypeData && (
                        <>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                                <h3 className="text-lg font-bold text-ink">{currentTypeData.displayName}</h3>
                                <div className="flex items-center gap-3">
                                    <span
                                        className="text-sm font-bold px-2.5 py-1 rounded-lg backdrop-blur-sm"
                                        style={{
                                            backgroundColor: `${interpolateRatingColor(currentTypeData.averageRating)}25`,
                                            color: interpolateRatingColor(currentTypeData.averageRating)
                                        }}
                                    >
                                        {currentTypeData.averageRating.toFixed(1)}/10
                                    </span>
                                    <span className="text-sm text-muted-foreground">
                                        {currentTypeData.totalRatings} {currentTypeData.totalRatings === 1 ? 'rating' : 'ratings'}
                                    </span>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {currentTypeData.ratings.map(rating => {
                                    const fullEntry = fullCoffeeEntries[rating._id];
                                    return (
                                        <div key={rating._id}>
                                            {fullEntry ? (
                                                <CoffeeCardWeb coffeeData={fullEntry} showUserOverlay={true} />
                                            ) : (
                                                <div className="bg-paper/60 backdrop-blur-sm rounded-xl border border-ink/10 p-4">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <div
                                                            className="px-3 py-2 rounded-lg text-paper font-bold text-base"
                                                            style={{
                                                                backgroundColor:
                                                                    rating.rateColor || interpolateRatingColor(rating.overallRating)
                                                            }}
                                                        >
                                                            {rating.overallRating.toFixed(1)}
                                                        </div>
                                                        <span className="text-sm text-muted-foreground">
                                                            {new Date(rating.createdAt).toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric'
                                                            })}
                                                        </span>
                                                    </div>
                                                    {rating.description && (
                                                        <p className="text-sm text-muted-foreground italic">
                                                            "{rating.description}"
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            )}
        </section>
    );
};

export default ShopRatingsCollapsed;
